import DomainVerificationIcon from "@mui/icons-material/DomainVerification"
import { Box, CircularProgress, MenuItem } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch, useAppSelector } from "hooks"
import { useState } from "react"
import licensePoolService from "services/licensePoolService"
import { setCurrentLicensePool, updateLicensePool } from "slices/licensePoolSlice"
import { updateLicense } from "slices/licenseSlice"
import { updateUser } from "slices/usersSlice"
import type { IAssignLicense } from "types/licenseTypes"
import type { IUser } from "types/userTypes"
import * as Yup from "yup"

type Props = {
  user: IUser
}

type AssingUserLicense = {
  licensePoolId: string
}

export function AssignLicenseDialog({ user }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)

  const licensePools = useAppSelector((state) => state.licensepool.licensePools)

  const dispatch = useAppDispatch()

  const initialValues: AssingUserLicense = {
    licensePoolId: "",
  }

  const assignLicenseValidationScheme = Yup.object({
    licensePoolId: Yup.string().required("Please set license pool"),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: assignLicenseValidationScheme,
    onSubmit: (values) => {
      setIsLoading(true)

      const licenseAssign: IAssignLicense = {
        assignedUserId: user.id,
      }

      licensePoolService
        .assignLicense(values.licensePoolId, licenseAssign)
        .then((license) => {
          const assignedUser = Object.assign({}, user, {
            licenseId: license.id,
          })
          dispatch(updateUser(assignedUser))
          dispatch(updateLicense(license))

          licensePoolService
            .getLicensePool(license.licensePoolId)
            .then((licensePool) => {
              dispatch(updateLicensePool(licensePool))
              dispatch(setCurrentLicensePool(licensePool))
              setIsLoading(false)
              handleClose()
            })
            .catch((err) => {
              console.log(err)
              setIsLoading(false)
            })

          setIsLoading(false)
          setOpen(false)
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <MenuItem onClick={handleClickOpen}>
        <DomainVerificationIcon sx={{ marginRight: 1 }} />
        Assign License
      </MenuItem>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Assign License</DialogTitle>
          <DialogContent>
            {licensePools.length > 0 ? (
              <TextField
                required
                fullWidth
                margin="dense"
                id="licensePoolId"
                label="License Pool"
                type="licensePoolId"
                name="licensePoolId"
                select
                variant="outlined"
                value={formik.values.licensePoolId}
                onChange={formik.handleChange}
                error={formik.touched.licensePoolId && Boolean(formik.errors.licensePoolId)}
                helperText={formik.touched.licensePoolId && formik.errors.licensePoolId}
              >
                {licensePools.map(
                  (licensePool) =>
                    licensePool.freeLicenses > 0 && (
                      <MenuItem key={licensePool.id} value={licensePool.id}>
                        {licensePool.name}
                      </MenuItem>
                    ),
                )}
              </TextField>
            ) : (
              "No License Pools Available"
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Assign
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
